import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface ScrollableContainerProps {
  minHeight: number;
  maxHeight: number;
  children: React.ReactNode;
  classStr: string;
  scrollable: boolean;
}

const ScrollableComponent: React.FC<ScrollableContainerProps> = ({
  minHeight,
  maxHeight,
  classStr,
  children,
  scrollable,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateFadeVisibility = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    setShowTopFade(scrollTop > 0);
    // Pixel count may be off by one due to rounding errors.
    setShowBottomFade(scrollTop + clientHeight + 1 < scrollHeight);
  };

  useEffect(() => {
    updateFadeVisibility(); // initial check

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateFadeVisibility);
    return () => el.removeEventListener("scroll", updateFadeVisibility);
  }, []);

  return (
    <Wrapper>
      {scrollable && showTopFade && <FadeOverlayTop />}
      <ScrollDiv
        ref={scrollRef}
        className={classStr}
        scrollable={scrollable}
        style={{
          minHeight: minHeight,
          maxHeight: maxHeight,
          flexWrap: "wrap",
          display: "flex",
          justifyContent: "space-between",
        }}>
        {children}
      </ScrollDiv>
      {scrollable && showBottomFade && <FadeOverlayBottom />}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

interface ScrollDivProps {
  scrollable: boolean;
}

const ScrollDiv = styled.div<ScrollDivProps>`
  position: relative;
  max-height: 300px;
  overflow-y: ${(props) => (props.scrollable ? "scroll" : "hidden")};
  scrollbar-gutter: stable;
  padding-right: 3px;
  box-sizing: content-box;
  scrollbar-width: thin;
  scrollbar-color: #aaa transparent;
`;

const FadeOverlayTop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 5px;
  height: 40px;
  pointer-events: none;
  background: linear-gradient(to top, rgba(255, 255, 255, 0), white);
  z-index: 1;
`;

const FadeOverlayBottom = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 5px;
  height: 40px;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), white);
  z-index: 1;
`;

export default ScrollableComponent;
